import { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@/lib/cn';
import useStepStore from '@/stores/steps';
import { type Step as StoreStep } from '@/stores/steps';

import { wizardComponents } from './components';

type StepWithIndex = StoreStep & { index: number };

const Steps = () => {
  const nextStep = useStepStore(({ nextStep }) => nextStep);
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentStep = useStepStore(({ stepData, step }) => ({
    ...stepData,
    index: step,
    content: stepData?.content || 'empty'
  }));

  const [content, setContent] = useState<StepWithIndex[]>([]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    if (!currentStep?.merge) {
      setContent([currentStep]);
      return;
    }

    setContent(previous => {
      if (previous.some(step => step.index === currentStep.index)) {
        return previous.slice(0, previous.findIndex(step => step.index === currentStep.index) + 1);
      }

      return [...previous, currentStep];
    });
  }, [currentStep]);

  useEffect(() => {
    if (content[content.length - 1]?.timeout) {
      const timeout = setTimeout(
        () => {
          nextStep();
        },
        content[content.length - 1].timeout
      );

      return () => clearTimeout(timeout);
    }
  }, [content, nextStep]);

  return (
    <AnimatePresence>
      <div
        ref={scrollRef}
        className='steps-scroll-container w-full flex-1 overflow-auto px-4 pt-6 lg:px-30 lg:[&:has(.product-selector)]:p-0! [&:has(.step-wrapper-no-padding)]:p-0!'
      >
        {content?.map(step => (
          <motion.div
            className={cn({
              'h-full!': step.fullHeight
            })}
            key={`step-${step.index}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }}
            onAnimationComplete={() => {
              if (!step.scroll) {
                return;
              }

              if (scrollRef.current) {
                scrollRef.current.scrollTo({
                  top: scrollRef.current.scrollHeight,
                  behavior: 'smooth'
                });
              }
            }}
          >
            {wizardComponents[step.content](step.data)}
          </motion.div>
        ))}
        <span className={cn('block h-30', { hidden: content.length === 1 })} />
      </div>
    </AnimatePresence>
  );
};

export default Steps;
